/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { parseDate } from './timeUtils';

/**
 * Returns whether the given configration is valid.
 * @param startTime The start time.
 * @param endTime The end time.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @returns Whether the given configuration is valid.
 */
const hasValidTimeConfig = (
  startTime: number,
  endTime: number,
  viewportStartTime?: number,
  viewportEndTime?: number
) => {
  const hasValidStartAndEnd = endTime > startTime;
  const hasValidViewport =
    viewportStartTime !== undefined && viewportEndTime !== undefined
      ? viewportEndTime > viewportStartTime
      : true;
  const hasValidViewportStart =
    viewportStartTime !== undefined
      ? viewportStartTime >= startTime && viewportStartTime < endTime
      : true;
  const hasValidViewportEnd =
    viewportEndTime !== undefined
      ? viewportEndTime > startTime && viewportEndTime <= endTime
      : true;
  return hasValidStartAndEnd && hasValidViewport && hasValidViewportStart && hasValidViewportEnd;
};

/**
 * Gets the parsed time axis params.
 * @param start The start date string.
 * @param end The end date string.
 * @param viewportStart The viewport start date string.
 * @param viewportEnd The viewport end date string.
 * @returns The parsed params.
 */
const getParsedTimeAxisParams = (
  start: string,
  end: string,
  viewportStart?: string,
  viewportEnd?: string
) => {
  const startTime = parseDate(start);
  const endTime = parseDate(end);
  const viewportStartTime = viewportStart ? parseDate(viewportStart) : undefined;
  const viewportEndTime = viewportEnd ? parseDate(viewportEnd) : undefined;
  const isValidTimeConfig = hasValidTimeConfig(
    startTime,
    endTime,
    viewportStartTime,
    viewportEndTime
  );
  return { startTime, endTime, viewportStartTime, viewportEndTime, isValidTimeConfig };
};

export { hasValidTimeConfig, getParsedTimeAxisParams };
